import React, { useEffect, useMemo, useState } from "react";
import TableGeneric from "../../../../shared/components/generic/table/table-generic";
import Pagination from "../../../../shared/components/generic/pagination/pagination";
import { adjustmentColumnsConfig } from "../../config/reajuste/adjustment-columns-config";
import { showConfirmAlert } from "../../../../shared/components/generic/alert/confirm-alert/confirm-alert";
import { showCommonAlert } from "../../../../shared/components/generic/alert/utils/show-common-alert-utils";
import AdminSection from "../../components/admin-section/admin-section";
import { useAdminFormModal } from "../../hooks/useAdminFormModal";
import FormModal from "../../../../shared/components/generic/form/form-modal/form-modal";
import { adjustmentFormConfig } from "../../config/reajuste/adjustment-form-config";
import { confirmSaveChanges, showNoRecordsFoundAlert } from "../../utils/admin-utils";
import { useAdjustment } from "../../hooks/reajuste/useAdjustment";
import { useAdjustmentUpdate } from "../../hooks/reajuste/useAdjustmentUpdate";
import { useAdjustments } from "../../hooks/reajuste/useAdjustments";
import { useAdjustmentById } from "../../hooks/reajuste/useAdjustmentById";
import { useAdjustmentDelete } from "../../hooks/reajuste/useAdjustmentDelete";
import {
	hasValidationErrors,
	removeClearedValidationErrors
} from "../../../utils/validation-utils";
import DateUtils from "../../../../shared/utils/DateUtils";
import PageLoading from "../../../../shared/components/generic/page-loading/page-loading";
import MonetaryUtils from "../../../../shared/utils/MonetaryUtils";

const isConfirmAccepted = result => {
	return result?.isConfirmed === true || result?.value === true;
};

const Reajuste = () => {
	const adjustmentModal = useAdminFormModal();

	const [adjustmentActionLoadingMessage, setAdjustmentActionLoadingMessage] = useState("");
	const [adjustmentValidationErrors, setAdjustmentValidationErrors] = useState({});
	const [clearedValidationErrors, setClearedValidationErrors] = useState({});
	const [adjustmentPagination, setAdjustmentPagination] = useState({
		totalElements: 0,
		totalPages: 0,
		page: 0,
		size: 10
	});

	const {
		adjustments,
		loading: adjustmentsLoading,
		refetch: fetchAdjustments
	} = useAdjustments();

	const {
		loading: adjustmentByIdLoading,
		fetchAdjustmentById
	} = useAdjustmentById();

	const {
		loading: createAdjustmentLoading,
		validateAdjustment,
		createAdjustment
	} = useAdjustment();

	const {
		loading: updateAdjustmentLoading,
		updateAdjustment
	} = useAdjustmentUpdate();

	const {
		loading: deleteAdjustmentLoading,
		deleteAdjustment
	} = useAdjustmentDelete();

	const isLoadingAdjustment =
		adjustmentsLoading ||
		adjustmentByIdLoading;

	const isSavingAdjustment =
		createAdjustmentLoading ||
		updateAdjustmentLoading;

	const isDeletingAdjustment = deleteAdjustmentLoading;

	const loadAdjustments = async (
		pagination = adjustmentPagination,
		{ showEmptyAlert = true } = {}
	) => {
		const result = await fetchAdjustments({
			page: pagination.page,
			size: pagination.size
		});

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar os reajustes.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return result;
		}

		setAdjustmentPagination(result.pagination);

		if (result.empty && showEmptyAlert) {
			await showNoRecordsFoundAlert();
		}

		return result;
	};

	useEffect(() => {
		loadAdjustments({
			page: 0,
			size: adjustmentPagination.size
		});
	}, []);

	const visibleAdjustmentValidationErrors = removeClearedValidationErrors({
		errors: adjustmentValidationErrors,
		clearedErrors: clearedValidationErrors
	});

	const handlePageChange = ({ selected }) => {
		loadAdjustments({
			...adjustmentPagination,
			page: selected
		});
	};

	const handleOpenCreate = () => {
		setAdjustmentValidationErrors({});
		setClearedValidationErrors({});
		adjustmentModal.openCreate();
	};

	const handleEdit = async row => {
		setAdjustmentValidationErrors({});
		setClearedValidationErrors({});

		const result = await fetchAdjustmentById(row.id);

		if (!result.ok) {
			await showCommonAlert({
				title: "Erro",
				message: result.error,
				fallbackMessage: "Não foi possível carregar os dados do reajuste.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		adjustmentModal.openEdit(result.adjustment);
	};

	const handleCloseAdjustmentModal = () => {
		setAdjustmentValidationErrors({});
		setClearedValidationErrors({});
		adjustmentModal.close();
	};

	const handleAdjustmentFieldChange = ({ name }) => {
		setClearedValidationErrors(previous => ({
			...previous,
			[name]: true
		}));
	};

	const handleSubmitAdjustment = async values => {
		const selectedItem = adjustmentModal.selectedItem;
		const isEditing = adjustmentModal.isEditing;

		const validationErrors = validateAdjustment(values);

		if (hasValidationErrors(validationErrors)) {
			setClearedValidationErrors({});
			setAdjustmentValidationErrors(validationErrors);

			const validationErrorMessages = [
				...new Set(Object.values(validationErrors))
			];

			await showCommonAlert({
				title: "Atenção",
				html: validationErrorMessages.join("<br />"),
				icon: "warning",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		setAdjustmentValidationErrors({});
		setClearedValidationErrors({});
		adjustmentModal.close();

		const confirmResult = await confirmSaveChanges({
			description: (
				<>
					Deseja salvar as alterações?
					<br />
					<strong>
						Esta ação afetará a Baseline de Net Sales do cliente, as faixas de segmentação e a Baseline de Net Sales do grupo.
					</strong>
				</>
			)
		});

		if (!isConfirmAccepted(confirmResult)) {
			return;
		}

		let saveResult;

		setAdjustmentActionLoadingMessage("Salvando reajuste...");

		try {
			saveResult = isEditing
				? await updateAdjustment(selectedItem.id, values)
				: await createAdjustment(values);

			if (saveResult.ok) {
				await loadAdjustments({
					page: isEditing ? adjustmentPagination.page : 0,
					size: adjustmentPagination.size
				});
			}
		} finally {
			setAdjustmentActionLoadingMessage("");
		}

		if (!saveResult.ok) {
			await showCommonAlert({
				title: "Erro",
				message: saveResult.error,
				fallbackMessage: "Não foi possível salvar o reajuste.",
				icon: "error",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			return;
		}

		await showCommonAlert({
			title: "Sucesso",
			message: isEditing
				? "Reajuste atualizado com sucesso."
				: "Reajuste incluído com sucesso.",
			icon: "success",
			customClass: {
				container: "common-alert__container",
				confirmButton: "alert-form-button"
			}
		});
	};

	const handleDelete = row => {
		const effectiveStartDate = DateUtils.formatDateToBrazilian(row.effectiveStartDate);
		const effectiveEndDate = DateUtils.formatDateToBrazilian(row.effectiveEndDate);

		showConfirmAlert({
			title: "Exclusão",
			description: `Tem certeza que deseja excluir o reajuste de ${MonetaryUtils.formatPercentage(row.adjustment)} com início em ${effectiveStartDate} e término em ${effectiveEndDate}?`,
			confirmLabel: "Sim, excluir",
			cancelLabel: "Cancelar"
		}).then(async result => {
			if (!isConfirmAccepted(result)) {
				return;
			}

			let deleteResult;
			let reloadResult;

			setAdjustmentActionLoadingMessage("Excluindo reajuste...");

			try {
				deleteResult = await deleteAdjustment(row.id);

				if (deleteResult.ok) {
					reloadResult = await loadAdjustments(
						{
							page: adjustmentPagination.page,
							size: adjustmentPagination.size
						},
						{
							showEmptyAlert: false
						}
					);
				}
			} finally {
				setAdjustmentActionLoadingMessage("");
			}

			if (!deleteResult.ok) {
				await showCommonAlert({
					title: "Erro",
					message: deleteResult.error,
					fallbackMessage: "Não foi possível excluir o reajuste.",
					icon: "error",
					customClass: {
						container: "common-alert__container",
						confirmButton: "alert-form-button"
					}
				});

				return;
			}

			await showCommonAlert({
				title: "Sucesso",
				message: "Reajuste excluído com sucesso.",
				icon: "success",
				customClass: {
					container: "common-alert__container",
					confirmButton: "alert-form-button"
				}
			});

			if (reloadResult?.empty) {
				await showNoRecordsFoundAlert();
			}
		});
	};

	const adjustmentColumns = useMemo(() => {
		return adjustmentColumnsConfig({
			onEdit: handleEdit,
			onDelete: handleDelete
		});
	}, []);

	const formFields = useMemo(() => {
		return adjustmentFormConfig({
			adjustment: adjustmentModal.selectedItem
		});
	}, [adjustmentModal.selectedItem]);

	return (
		<>
			<PageLoading
				disabled={adjustmentModal.isOpen}
				actionMessage={adjustmentActionLoadingMessage}
				states={[
					{
						loading: isLoadingAdjustment,
						message: "Carregando reajustes..."
					},
					{
						loading: isDeletingAdjustment,
						message: "Excluindo reajuste..."
					},
					{
						loading: isSavingAdjustment,
						message: "Salvando reajuste..."
					}
				]}
			/>
			<AdminSection
				title="Reajuste"
				actionLabel="Incluir reajuste"
				actionStartIcon="plusCircle"
				onAction={handleOpenCreate}
			>
				<TableGeneric
					columns={adjustmentColumns}
					data={adjustments}
					rowKey="id"
				/>

				{adjustmentPagination.totalPages > 1 && (
					<Pagination
						pageCount={adjustmentPagination.totalPages}
						currentPage={adjustmentPagination.page}
						onPageChange={handlePageChange}
					/>
				)}
			</AdminSection>

			<FormModal
				open={adjustmentModal.isOpen}
				title={adjustmentModal.isEditing ? "Editar reajuste" : "Incluir reajuste"}
				formId="adjustment-form"
				formFields={formFields}
				errors={visibleAdjustmentValidationErrors}
				onClose={handleCloseAdjustmentModal}
				onSubmit={handleSubmitAdjustment}
				onFieldChange={handleAdjustmentFieldChange}
				submitLabel="Salvar"
				cancelLabel="Cancelar"
				submitDisabled={
					isSavingAdjustment ||
					isDeletingAdjustment ||
					isLoadingAdjustment ||
					Boolean(adjustmentActionLoadingMessage)
				}
			/>
		</>
	);
};

export default Reajuste;